import {
  OreHeading,
  OreInput,
  OreMap,
  OreMapMarker,
  OreMessageBar,
  OreStack,
} from '@edenredespana/oreneta'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { handleErrors } from '@/Shared/ui/form'
import { StoreForm, TransformObject, useGooglePlaces } from '@/Store/ui/hooks'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { MerchantsDataModel } from '@/User/domain'

interface MyStoreAddressFormRestaurantProps {
  readOnly?: boolean
  noMap?: boolean
  show: boolean
  merchant: MerchantsDataModel
}

export const MyStoreAddressFormRestaurant = ({
  readOnly = false,
  noMap = false,
  show,
  merchant,
}: MyStoreAddressFormRestaurantProps): JSX.Element => {
  const { t, i18n } = useTranslation()

  const formContext = useFormContext<StoreForm>()
  const { register, formState, getValues } = formContext
  const { errors } = handleErrors(formState)
  const { ref, ...rest } = register('street')

  const transformObject: TransformObject<StoreForm> = {
    street: 'street',
    number: 'number',
    floorBlockStair: 'floorBlockStair',
    postalCode: 'postalCode',
    city: 'city',
    province: 'province',
    latitude: 'latitude',
    longitude: 'longitude',
  }

  const inputReference = useRef<HTMLInputElement | null>(null)
  const { initGooglePlaces } = useGooglePlaces({ formContext, transformObject })

  useEffect(() => {
    !readOnly && initGooglePlaces(inputReference)
  }, [])

  return (
    <OreStack placeContent="stretch" space="large">
      {!readOnly ? (
        <OreHeading as="h2" size="headline-md">
          {t(storesTranslation.myStore.form.address.title)}
        </OreHeading>
      ) : null}
      {!readOnly ? (
        <OreMessageBar color="warning">
          {!merchant.isNursery ? 
          t(storesTranslation.myStore.form.address.warning) : 
          t(storesTranslation.myStore.form.address.warningGuarderia)}
        </OreMessageBar>
      ) : null}
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...rest}
          {...errors('street')}
          id="street"
          label={t(storesTranslation.myStore.form.address.street.label)}
          placeholder={t(storesTranslation.myStore.form.address.street.placeholder)}
          required
          ref={(input: HTMLInputElement) => {
            inputReference.current = input
            ref(input)
          }}
          defaultValue={formContext.getValues('street') ?? ''}
          labelTooltip={
            !readOnly && !merchant.isNursery
              ? t(storesTranslation.myStore.form.address.street.tooltip)
              : undefined
          }
          maxLength={255}
          disabled={readOnly || !show}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('number')}
          {...errors('number')}
          id="number"
          label={t(storesTranslation.myStore.form.address.number.label)}
          disabled
        />
        <OreInput
          {...register('floorBlockStair')}
          {...errors('floorBlockStair')}
          id="floorBlockStair"
          label={merchant.isNursery ? t(storesTranslation.myStore.form.address.floorBlockStair.guarderiaLabel) : 
            t(storesTranslation.myStore.form.address.floorBlockStair.label)}
          disabled
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('postalCode')}
          {...errors('postalCode')}
          id="postalCode"
          label={t(storesTranslation.myStore.form.address.postalCode.label)}
          disabled
        />
        <OreInput
          {...register('city')}
          {...errors('city')}
          id="city"
          label={t(storesTranslation.myStore.form.address.city.label)}
          disabled
        />
        <OreInput
          {...register('province')}
          {...errors('province')}
          id="province"
          label={t(storesTranslation.myStore.form.address.province.label)}
          disabled
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...register('additionalInformation')}
          {...errors('additionalInformation')}
          id="additionalInformation"
          label={t(storesTranslation.myStore.form.address.additionalInfo.label)}
          placeholder={t(
            storesTranslation.myStore.form.address.additionalInfo.placeholder,
          )}
          maxLength={255}
          disabled={readOnly || !show}
        />
      </OreStack>
      {!noMap && getValues('latitude') && getValues('longitude') ? (
        <div
          style={{
            height: '260px',
            width: '100%',
            position: 'relative',
          }}
        >
          <OreMap
            loaderOptions={{
              apiKey: import.meta.env.VITE_GOOGLE_KEY,
              language: i18n.language,
              region: 'ES',
            }}
            center={{
              lat: Number(getValues('latitude')),
              lng: Number(getValues('longitude')),
            }}
            zoom={16}
            mapOptions={{
              disableDefaultUI: true,
              mapTypeControl: false,
              zoomControl: true,
              panControl: false,
              disableDoubleClickZoom: true,
              gestureHandling: 'none',
            }}
          >
            <OreMapMarker
              position={{
                lat: Number(getValues('latitude')),
                lng: Number(getValues('longitude')),
              }}
              title={getValues('merchantName')}
              onClick={marker => {
                const infoWindow = new google.maps.InfoWindow({
                  content: getValues('merchantName'),
                  ariaLabel: getValues('merchantName'),
                })

                infoWindow.open({
                  anchor: marker,
                  map: marker.map,
                })
              }}
            />
          </OreMap>
        </div>
      ) : null}
    </OreStack>
  )
}
